import cx from 'classnames';
import {useEffect, useMemo, useRef, useState} from 'react';
import type {Relative} from '~/GalleryPB/types/gallery';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {getColors} from '~library/utils/styles';
import * as styles from './RelatedCategoriesPills.module.scss';

const colors = getColors();

interface RelatedCategoriesPillsProps {
  categories: Relative[];
}

const SCROLL_EDGE_TOLERANCE = 10;
const SCROLL_FACTOR = 0.6;

const joinPath = (base: string, segment: string) => {
  const b = base.replace(/\/+$/, ''); // Remove trailing slashes from base
  const s = segment.replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes from segment
  return `${b}/${s}/`; // Join with single slash and add trailing slash
};

const currentFilterType = () => (window.cards_page ? 'card' : 'invitation');

const buildUrlForCategory = (label: string, categoryPath: string) => {
  const url = new URL(window.location.href);

  const segments = url.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (segments.length > 1) segments.pop();
  const base = `/${segments.join('/')}/`;

  const newPath = joinPath(base, categoryPath);

  const params = new URLSearchParams();
  params.set('filter', currentFilterType());
  params.set('selectedCat', label);

  return `${newPath}?${params.toString()}`;
};

const selectedFromUrl = (categories: Relative[]) => {
  const params = new URLSearchParams(window.location.search);
  const selectedFromQuery = params.get('selectedCat');

  if (selectedFromQuery) return decodeURIComponent(selectedFromQuery);

  const segments = window.location.pathname.split('/').filter(Boolean);
  const lastSegments = segments[segments.length - 1] || '';

  for (const category of categories) {
    if (lastSegments === category.categoryPath.replace(/^\/|\/$/g, '')) {
      return category.displayName;
    }
  }

  return null;
};

export const RelatedCategoriesPills = ({categories}: RelatedCategoriesPillsProps) => {
  const scrollRef = useRef<HTMLUListElement>(null);
  const [hasScrollLeft, setHasScrollLeft] = useState(false);
  const [hasScrollRight, setHasScrollRight] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const normalizedCategories = useMemo(() => {
    const normalized = categories.map((item) => ({
      id: item.categoryId ?? `${item.categoryPath}-${item.displayName}`,
      label: item.displayName,
      value: item.categoryPath,
      selected: item.displayName === selectedCategory,
    }));

    normalized.sort((a, b) => Number(b.selected) - Number(a.selected));

    return normalized;
  }, [categories, selectedCategory]);

  useEffect(() => {
    setSelectedCategory(selectedFromUrl(categories));
  }, [categories]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * SCROLL_FACTOR;
    const newScrollLeft =
      direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const checkScroll = () => {
      const container = scrollRef.current;
      if (!container) return;

      const {scrollLeft, scrollWidth, clientWidth} = container;

      setHasScrollLeft(scrollLeft > SCROLL_EDGE_TOLERANCE);
      setHasScrollRight(scrollLeft < scrollWidth - clientWidth - SCROLL_EDGE_TOLERANCE);
    };

    checkScroll();

    container.addEventListener('scroll', checkScroll, {passive: true});
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset scroll position of container when categories change
  useEffect(() => {
    // Reset scroll position when categories change
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({left: 0, top: 0, behavior: 'instant'});
  }, [normalizedCategories]);

  if (!categories || categories.length === 0) return null;
  return (
    <nav
      className={cx(styles.relatedCategoriesWrapper, {
        [styles.hasScrollLeft]: hasScrollLeft,
        [styles.hasScrollRight]: hasScrollRight,
      })}
      aria-label="Related Categories"
    >
      {hasScrollLeft && (
        <Button
          className={styles.relatedCategoriesNav}
          variant="unstyled-button"
          ariaLabel="Scroll categories left"
          data-qa-id="relatedCategoriesScrollLeft"
          onClick={() => scroll('left')}
          icon={<ChevronIcon direction="left" color={colors.charcoal} ratio={0.5} />}
        />
      )}
      <ul
        ref={scrollRef}
        className={cx(styles.relatedCategories, {
          [styles.hasScrollLeft]: hasScrollLeft,
          [styles.hasScrollRight]: hasScrollRight,
        })}
      >
        {normalizedCategories.map((category) => {
          const isActive = category.label === selectedCategory;
          const href = buildUrlForCategory(category.label, category.value);
          return (
            <li key={`${category.id}`}>
              <Link
                href={href}
                as="secondary"
                className={cx(styles.relatedCategoryPill, {[styles.active]: isActive})}
                data-qa-id={`relatedCategory-${category.label}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Typography variant="paragraph3" as="span" className={styles.label}>
                  {category.label}
                </Typography>
              </Link>
            </li>
          );
        })}
      </ul>
      {hasScrollRight && (
        <Button
          className={styles.relatedCategoriesNav}
          variant="unstyled-button"
          size="small"
          ariaLabel="Scroll categories right"
          data-qa-id="relatedCategoriesScrollRight"
          onClick={() => scroll('right')}
          icon={<ChevronIcon direction="right" color={colors.charcoal} ratio={0.5} />}
        />
      )}
    </nav>
  );
};
